import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGetAgentHisotryMutation,
  useGetAiWorkerByIdQuery,
  useTypedSelector,
  useAppDispatch,
  setTemplates,
  setUXGenieLoading,
  useEntityResourcePermissions,
} from '@circuitry-ai/doc-data';
import { Card, CardHeader, CardBody, CardFooter, Divider, Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton, ErrorBoundary } from '@circuitry-ai/doc-ui';

interface UsageTemplate {
  aiworker_usage_id: string;
  aiworker_id: string;
  title: string;
  aiworker_name: string;
  status: string;
  used_at: string;
  date: string;
  user_id: string;
  user_name: string;
  likes_count?: number;
  dislikes_count?: number;
  comments_count?: number;
  tag_values?: string[];
  used_at_timestamp?: number;
  agent_score?: string;
  quality?: string;
  aiworker_type?: string;
  input_data?: { user_input: string };
  output_data?: {
    generated_code?: string;
    generated_schema: string;
    action_name: string;
    saved_at: string;
    output_type?: string;
    storage?: 's3';
    html_file_name?: string;
  };
  created_at?: string;
  updated_at?: string;
}

// --- Helpers ---
const getTemplateTitle = (template: UsageTemplate): string =>
  template.title && template.title.trim() !== ''
    ? template.title
    : (template.output_data?.action_name || template.aiworker_name || 'Unknown Template');

const getTemplateDate = (template: UsageTemplate): Date =>
  new Date(template.date || template.used_at || template.output_data?.saved_at || 0);

const formatVersionDatetime = (template: UsageTemplate): string => {
  const d = getTemplateDate(template);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// --- Single versioned template card ---
interface VersionedTemplateCardProps {
  versions: UsageTemplate[]; // ALL versions for this worker, sorted newest first
  onView: (template: UsageTemplate) => void;
  onEdit: (template: UsageTemplate) => void;
  t: any;
  canEdit?: boolean;
}

const VersionedTemplateCard: React.FC<VersionedTemplateCardProps> = ({ versions, onView, onEdit, t, canEdit = false }) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const template = versions[selectedIdx];

  const title = getTemplateTitle(versions[0]);
  const status = template.status || 'unknown';

  const getStatusColor = (s: string) => {
    const sl = s?.toLowerCase() || '';
    if (sl.includes('succeed') || sl === 'completed') return 'bg-green-100 text-green-700';
    if (sl.includes('progress') || sl === 'processing') return 'bg-blue-100 text-blue-700';
    if (sl.includes('fail') || sl === 'error') return 'bg-red-100 text-red-700';
    return 'bg-default-100 text-default-700';
  };

  const formatStatus = (s: string) =>
    s?.charAt(0).toUpperCase() + s?.slice(1).toLowerCase() || 'Unknown';

  return (
    <Card className="border shadow-sm w-full max-w-xs" shadow="none">
      <CardHeader className="flex justify-between items-start pb-2">
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-2">
            <Icon icon="mdi:file-code" className="text-blue-600" width={20} />
            <span className="text-foreground font-semibold text-base">{title}</span>
          </div>
          <span className="text-xs text-default-400">
            {versions.length} version{versions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getStatusColor(status)}`}>
          <Icon icon="mdi:circle" width={8} />
          {formatStatus(status)}
        </div>
      </CardHeader>

      <CardBody className="pt-0 flex flex-col gap-3">
        {/* Current selected version user info */}
        <div className="flex items-center gap-2 text-xs text-default-500">
          <Icon icon="mdi:account" width={13} />
          <span>{template.user_name || template.user_id || 'N/A'}</span>
        </div>

        {/* Version selector */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-default-500">Version</span>
          <select
            value={selectedIdx}
            onChange={(e) => setSelectedIdx(Number(e.target.value))}
            className="text-sm border border-default-200 rounded-lg px-2 py-1.5 text-default-700 bg-content1 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
          >
            {versions.map((v, idx) => (
              <option key={v.aiworker_usage_id} value={idx}>
                {idx === 0 ? `Latest — ${formatVersionDatetime(v)}` : formatVersionDatetime(v)}
              </option>
            ))}
          </select>
        </div>
      </CardBody>

      <Divider />

      <CardFooter className="flex gap-2">
        <CtyButton
          className="flex-1"
          variant="bordered"
          radius="sm"
          size="md"
          color="default"
          onPress={() => onView(template)}
        >
          <Icon icon="mdi:eye" width={16} className="mr-1" />
          {t('View')}
        </CtyButton>
        <CtyButton
          className="flex-1"
          variant="solid"
          radius="sm"
          size="md"
          color="primary"
          onPress={() => onEdit(template)}
          isDisabled={!canEdit}
        >
          <Icon icon="mdi:pencil" width={16} className="mr-1" />
          {t('Edit')}
        </CtyButton>
      </CardFooter>
    </Card>
  );
};

interface TemplateListContentProps {
  templates: UsageTemplate[];
  onView: (template: UsageTemplate) => void;
  onEdit: (template: UsageTemplate) => void;
  t: any;
  canEdit?: boolean;
}

const TemplateListContent: React.FC<TemplateListContentProps> = ({ templates, onView, onEdit, t, canEdit = false }) => {
  // All templates for this worker are versions — sort newest first
  const sortedVersions = useMemo(
    () => [...templates].sort((a, b) => getTemplateDate(b).getTime() - getTemplateDate(a).getTime()),
    [templates]
  );

  if (sortedVersions.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-full border-2 border-dashed border-default-300 rounded-lg m-4 bg-default-100 p-8">
        <Icon icon="mdi:file-document-remove-outline" className="text-default-400 mb-4" width={64} />
        <div className="text-xl text-default-500 font-medium mb-2">
          {t('No Templates Found')}
        </div>
        <p className="text-sm text-default-500 text-center max-w-md">
          {t('No templates have been created with this UXGenie Template yet. Click "Create Template" to get started.')}
        </p>
      </div>
    );
  }

  return (
    <VersionedTemplateCard
      versions={sortedVersions}
      onView={onView}
      onEdit={onEdit}
      t={t}
      canEdit={canEdit}
    />
  );
};

const TemplateListContentWithErrorBoundary = ErrorBoundary(TemplateListContent);

export const UXGenieTemplateHistory: React.FC<{ aiworkerId?: string; basePath?: string }> = ({ aiworkerId: propAiworkerId, basePath: propBasePath }) => {
  const basePath = propBasePath ?? '/uxgenie';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { aiworker_id: routeAiworkerId } = useParams<{ aiworker_id: string }>();
  const token = useTypedSelector((state) => state.auth.token);

  const aiworker_id = propAiworkerId || routeAiworkerId || import.meta.env.CTY_UXGENIE_AIWORKER_ID;
  const aiworker_type = import.meta.env.CTY_UXGENIE_AIWORKER_TYPE;

  const { canUpdate } = useEntityResourcePermissions('uxgenie', aiworker_id || undefined);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [templates, setTemplatesLocal] = useState<UsageTemplate[]>([]);

  const { data: workerData, isLoading: isLoadingWorker } = useGetAiWorkerByIdQuery(aiworker_id || '', {
    skip: !aiworker_id,
  });

  const [getUsageHistory, { isLoading: isLoadingHistory }] = useGetAgentHisotryMutation();

  useEffect(() => {
    if (!(aiworker_id && token)) return;

    dispatch(setUXGenieLoading(true));

    // Capture the mutation handle so it can be aborted on cleanup — otherwise an
    // effect re-run (React StrictMode's dev double-invoke, or a remount) leaves the
    // stale request running to completion, producing duplicate network calls.
    const request = getUsageHistory({
      pagination: { page: 1, per_page: 50 },
      filters: [
        { aiworker_type: aiworker_type, condition: 'contains' },
        { aiworker_id: aiworker_id, condition: 'contains' },
      ],
    });

    request
      .unwrap()
      .then((response) => {
        let usageData: UsageTemplate[] = [];

        if (response?.aiworkers_conversations && Array.isArray(response.aiworkers_conversations)) {
          usageData = response.aiworkers_conversations;
        } else if (response?.data?.aiworkers_conversations && Array.isArray(response.data.aiworkers_conversations)) {
          usageData = response.data.aiworkers_conversations;
        } else if (Array.isArray(response?.data)) {
          usageData = response.data;
        } else if (Array.isArray(response?.items)) {
          usageData = response.items;
        } else if (Array.isArray(response)) {
          usageData = response;
        }

        setTemplatesLocal(usageData);
        dispatch(setTemplates(usageData));
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('Failed to fetch usage history:', error);
        setTemplatesLocal([]);
        dispatch(setUXGenieLoading(false));
      });

    return () => {
      request.abort();
    };
  }, [aiworker_id, token, getUsageHistory, dispatch, aiworker_type]);

  const filteredTemplates = useMemo(() => {
    let filtered = templates.filter((t) => t.aiworker_id === aiworker_id);
    if (searchQuery.trim()) {
      filtered = filtered.filter((t) =>
        getTemplateTitle(t).toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return filtered;
  }, [templates, searchQuery, aiworker_id]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleViewTemplate = async (template: UsageTemplate) => {
    navigate(`${basePath}/view/${template.aiworker_usage_id}`);
  };

  const handleEditTemplate = async (template: UsageTemplate) => {
    navigate(`${basePath}/${aiworker_id}/edit/${template.aiworker_usage_id}`, {
      state: {
        uxgenieWorker: workerData,
        template: template,
        editMode: true
      }
    });
  };

  const handleCreateTemplate = () => {
    navigate(`${basePath}/${aiworker_id}/execute`, {
      state: { uxgenieWorker: workerData }
    });
  };

  const handleBack = () => {
    if (routeAiworkerId || (basePath && aiworker_id)) {
      navigate(basePath);
    }
  };

  const isLoading = isLoadingWorker || isLoadingHistory;

  return (
    <div className="relative flex flex-col h-full gap-4 p-4">
      {/* Create button — fixed to top-right, out of flow */}
      <div className="absolute top-4 right-4 z-10">
        <Button
          color="primary"
          variant="solid"
          className="font-bold"
          size="sm"
          onPress={handleCreateTemplate}
          startContent={<Icon icon="fa6-solid:plus" width={15} />}
        >
          {t('Create Blueprint')}
        </Button>
      </div>

      {/* Template list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <Icon icon="eos-icons:loading" className="text-blue-500" width={48} />
              <span className="text-default-500">{t('Loading templates...')}</span>
            </div>
          </div>
        ) : (
          <TemplateListContentWithErrorBoundary
            templates={filteredTemplates}
            onView={handleViewTemplate}
            onEdit={handleEditTemplate}
            t={t}
            canEdit={canUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default UXGenieTemplateHistory;
